/**
 * LAS NOTAS DEL ENTRENADOR, EN LA PESTAÑA MACROS DE LA FICHA (hoja del 22-10, fila 9).
 *
 * «Campo de texto. Se puede añadir una nota que el cliente no verá. Por defecto muestra las
 * últimas 5. Muestra un botón para "Mostrar todas".»
 *
 * Enseña juntas las notas sueltas que se escriben aquí y las que se escribieron en el
 * check-in de un día (esas se siguen escribiendo en el check-in). Qué entra en la lista y en
 * qué orden lo decide el servidor (`core/notas_del_entrenador`); aquí solo se pinta.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { mensajeDeError } from '../lib/mensajeDeError';
import { diaCorto } from '../lib/detalleDelCierre';
import { hoyLocal } from '../lib/horaEspana';

// El id lo pone la pantalla y va con el borrador: si el guardado falla (o la respuesta no
// llega) y se vuelve a pulsar con el mismo texto, sale con el mismo id y el servidor no crea
// otra. Cambia solo al tocar el texto o al guardarse bien.
const idNuevo = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

const NotasDelEntrenador = ({ api, clientId }) => {
    const [notas, setNotas] = useState([]);
    const [aLaVista, setALaVista] = useState(5);
    const [todas, setTodas] = useState(false);
    const [texto, setTexto] = useState('');
    const [pendiente, setPendiente] = useState(null);   // el id de la que se está guardando
    const [cargando, setCargando] = useState(true);
    const idDelBorrador = useRef(null);

    const cargar = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/clients/${clientId}/notas-del-entrenador`);
            setNotas(data?.notas || []);
            if (data?.a_la_vista) setALaVista(data.a_la_vista);
        } catch (e) {
            console.error('[notas del entrenador] no se pudieron leer', e);
        } finally {
            setCargando(false);
        }
    }, [api, clientId]);

    useEffect(() => { cargar(); }, [cargar]);

    const guardar = async () => {
        if (!texto.trim() || pendiente) return;
        if (!idDelBorrador.current) idDelBorrador.current = idNuevo();
        const id = idDelBorrador.current;
        setPendiente(id);
        try {
            await api.post(`/admin/clients/${clientId}/notas-del-entrenador`, { id, texto });
            idDelBorrador.current = null;
            setTexto('');
            await cargar();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No hemos podido guardar la nota. Inténtalo en un momento.'));
            // Puede que sí se guardara y solo se perdiera la respuesta: se relee la lista.
            cargar();
        } finally {
            setPendiente(null);
        }
    };

    const visibles = todas ? notas : notas.slice(0, aLaVista);
    return (
        <section className="rounded-xl border border-[#E5E2DB] bg-white p-4 space-y-3" data-testid="notas-del-entrenador">
            <div>
                <h3 className="text-sm font-bold text-[#1C1B19]">Notas del entrenador</h3>
                <p className="text-xs text-[#6F6B64]">El cliente no las ve.</p>
            </div>
            <div>
                <textarea value={texto} onChange={(e) => { idDelBorrador.current = null; setTexto(e.target.value); }} rows={3} maxLength={4000}
                    data-testid="nota-nueva-del-entrenador" aria-label="Nota nueva del entrenador"
                    placeholder="Escribe una nota sobre este cliente..."
                    className="w-full bg-[#FEF9E7] border border-[#F1DFA0] rounded-lg px-3 py-2 text-sm text-[#1C1B19] focus:outline-none focus:border-[#FF671F]" />
                <div className="flex justify-end mt-1">
                    <button type="button" onClick={guardar} disabled={!texto.trim() || !!pendiente}
                        data-testid="guardar-nota-nueva"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#FF671F] text-white disabled:opacity-40">
                        {pendiente ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Añadir nota
                    </button>
                </div>
            </div>
            {cargando ? (
                <p className="text-xs text-[#6F6B64]">Cargando notas...</p>
            ) : notas.length === 0 ? (
                <p className="text-xs text-[#6F6B64]" data-testid="sin-notas">Todavía no hay notas.</p>
            ) : (
                <ul className="space-y-2" data-testid="lista-de-notas">
                    {visibles.map((n) => (
                        <li key={n.id} className="rounded-lg bg-[#F6F5F2] border border-[#E5E2DB] px-3 py-2">
                            <p className="text-sm text-[#1C1B19] whitespace-pre-wrap break-words">{n.texto}</p>
                            <p className="text-[11px] text-[#6F6B64] mt-1">
                                {n.dia ? diaCorto(n.dia, hoyLocal()) : 'Sin fecha'}
                                {n.de_un_dia ? ' · en el check-in de ese día' : ''}
                                {n.autor ? ` · ${n.autor}` : ''}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
            {notas.length > aLaVista && (
                <button type="button" onClick={() => setTodas((v) => !v)} data-testid="mostrar-todas-las-notas"
                    className="text-xs font-semibold text-[#C2410C] hover:underline">
                    {todas ? 'Mostrar menos' : `Mostrar todas (${notas.length})`}
                </button>
            )}
        </section>
    );
};

export default NotasDelEntrenador;
